<template>
  <div class="mc-portal">
    <PortalTopBar role="staff" />

    <div class="pt-container">
      <div class="pt-head">
        <div>
          <div class="pt-kicker">Your account</div>
          <h1 class="pt-title">Payments</h1>
          <p class="pt-sub">Every payment to you, with invoices and a CSV export for your records.</p>
        </div>
        <button type="button" class="pt-btn pt-btn-outline" :disabled="!filteredPayments.length" @click="exportCSV">
          <SiteIcon name="download" :size="17" /> Export CSV
        </button>
      </div>

      <div class="kpis">
        <div class="kpi">
          <div class="kpi-label">Total paid</div>
          <div class="kpi-value">{{ money(totalPaid) }}</div>
          <div class="kpi-meta">{{ paidCount }} {{ paidCount === 1 ? "payment" : "payments" }}</div>
        </div>
        <div class="kpi amber">
          <div class="kpi-label">Outstanding</div>
          <div class="kpi-value">{{ money(totalOutstanding) }}</div>
          <div class="kpi-meta">{{ outstandingCount }} not paid yet</div>
        </div>
        <div class="kpi navy">
          <div class="kpi-label">Last payment</div>
          <div class="kpi-value small">{{ lastPaymentDate ? formatDay(lastPaymentDate) : "—" }}</div>
          <svg class="spark" viewBox="0 0 200 40" preserveAspectRatio="none" aria-hidden="true">
            <polyline :points="sparklinePoints" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <div class="kpi-meta">Last {{ lastAmounts.length }} amounts</div>
        </div>
      </div>

      <section class="pt-card">
        <div class="pt-card-head">
          <h2 class="pt-card-title"><SiteIcon name="card" :size="20" /> History <span class="pt-count">{{ filteredPayments.length }}</span></h2>
          <div class="filters">
            <label class="pt-field">
              <span class="pt-label">Status</span>
              <select v-model="statusFilter" class="pt-select">
                <option value="">All</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </label>
            <label class="pt-field">
              <span class="pt-label">From</span>
              <input v-model="startDate" type="date" class="pt-input" />
            </label>
            <label class="pt-field">
              <span class="pt-label">To</span>
              <input v-model="endDate" type="date" class="pt-input" />
            </label>
          </div>
        </div>

        <div v-if="loading">
          <div v-for="n in 4" :key="n" class="pt-skeleton"></div>
        </div>

        <div v-else-if="message" class="pt-notice error">
          <SiteIcon name="alert" :size="17" /> <span>{{ message }}</span>
        </div>

        <div v-else-if="!filteredPayments.length" class="pt-empty">
          <SiteIcon name="card" :size="40" />
          <h3>No payments yet</h3>
          <p>{{ payments.length ? "Nothing matches these filters." : "Payments to you will appear here." }}</p>
        </div>

        <div v-else class="pt-table">
          <div class="pt-thead cols">
            <span>Amount</span>
            <span>Status</span>
            <span>Date</span>
            <span>Description</span>
            <span>Invoice</span>
          </div>
          <div v-for="p in filteredPayments" :key="p.id" class="pt-tr cols">
            <b class="amount">{{ money(p.amount) }}</b>
            <span><span class="pt-badge" :class="tone(p.status)">{{ prettyStatus(p.status) }}</span></span>
            <span class="muted">{{ formatDate(p.timestamp) }}</span>
            <span class="desc">{{ p.description || p.memo || "—" }}</span>
            <span>
              <a v-if="p.invoiceUrl" :href="p.invoiceUrl" target="_blank" rel="noopener" class="pt-btn pt-btn-ghost pt-btn-sm">
                <SiteIcon name="file" :size="15" /> View
              </a>
              <span v-else class="muted">—</span>
            </span>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import "@/styles/portal.css";
import { auth, db } from "@/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import PortalTopBar from "@/components/PortalTopBar.vue";
import SiteIcon from "@/components/SiteIcon.vue";

export default {
  name: "StaffPayments",
  components: { PortalTopBar, SiteIcon },
  data() {
    return {
      payments: [],
      loading: true,
      message: "",
      statusFilter: "",
      startDate: null,
      endDate: null,
    };
  },
  computed: {
    paidOnly() {
      return this.payments.filter((p) => (p.status || "").toLowerCase() === "paid");
    },
    totalPaid() {
      return this.paidOnly.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    },
    unpaid() {
      return this.payments.filter((p) => !["paid", "refunded"].includes((p.status || "").toLowerCase()));
    },
    totalOutstanding() {
      return this.unpaid.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    },
    paidCount() {
      return this.paidOnly.length;
    },
    outstandingCount() {
      return this.unpaid.length;
    },
    lastPaymentDate() {
      const arr = this.paidOnly.slice().sort((a, b) => this.tsMs(b.timestamp) - this.tsMs(a.timestamp));
      return arr[0]?.timestamp || null;
    },
    lastAmounts() {
      const arr = this.payments.slice().sort((a, b) => this.tsMs(a.timestamp) - this.tsMs(b.timestamp));
      const vals = arr.slice(-12).map((p) => Number(p.amount) || 0);
      return vals.length ? vals : [0];
    },
    sparklinePoints() {
      const vals = this.lastAmounts;
      if (vals.length < 2) return "0,38 200,38";
      const max = Math.max(...vals, 1);
      return vals.map((v, i) => `${(i / (vals.length - 1)) * 200},${38 - (v / max) * 32}`).join(" ");
    },
    filteredPayments() {
      const s = (this.statusFilter || "").toLowerCase();
      const start = this.startDate ? new Date(this.startDate) : null;
      const end = this.endDate ? new Date(this.endDate) : null;
      if (end) end.setHours(23, 59, 59, 999);
      return this.payments
        .filter((p) => {
          const t = this.tsMs(p.timestamp);
          return (!s || (p.status || "").toLowerCase() === s) && (!start || t >= start.getTime()) && (!end || t <= end.getTime());
        })
        .sort((a, b) => this.tsMs(b.timestamp) - this.tsMs(a.timestamp));
    },
  },
  mounted() {
    this._unsubAuth = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        this.$router.push("/login");
        return;
      }
      try {
        const qs = await getDocs(query(collection(db, "staffPayments"), where("staffUid", "==", user.uid)));
        this.payments = qs.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (e) {
        console.error("Error fetching payment history:", e);
        this.message = "Could not load your payment history. Please try again.";
      } finally {
        this.loading = false;
      }
    });
  },
  beforeDestroy() {
    if (this._unsubAuth) this._unsubAuth();
  },
  methods: {
    money(n) {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(Number(n || 0));
    },
    tsMs(ts) {
      if (!ts) return 0;
      if (ts.seconds) return ts.seconds * 1000;
      if (ts.toDate) return ts.toDate().getTime();
      return new Date(ts).getTime();
    },
    formatDate(ts) {
      const ms = this.tsMs(ts);
      return ms ? new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "";
    },
    formatDay(ts) {
      const ms = this.tsMs(ts);
      return ms ? new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
    },
    prettyStatus(s = "") {
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Unknown";
    },
    tone(s = "") {
      return { paid: "green", pending: "amber", failed: "red", refunded: "outline", unpaid: "" }[(s || "").toLowerCase()] || "";
    },
    exportCSV() {
      const rows = [
        ["Amount", "Status", "Date", "Description", "Invoice URL"],
        ...this.filteredPayments.map((p) => [
          (Number(p.amount) || 0).toFixed(2),
          this.prettyStatus(p.status || ""),
          this.formatDate(p.timestamp),
          p.description || p.memo || "",
          p.invoiceUrl || "",
        ]),
      ];
      const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payments_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 22px;
}

.kpi {
  padding: 18px 20px;
  border: 1px solid var(--rule);
  border-top: 3px solid var(--green);
  border-radius: 4px 4px 10px 10px;
  background: var(--card);
}

.kpi.amber {
  border-top-color: var(--brass);
}

.kpi.navy {
  border-top-color: var(--ink);
}

.kpi-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

.kpi-value {
  margin: 8px 0 2px;
  font-family: var(--serif);
  font-size: 2.3rem;
  font-weight: 700;
  line-height: 1.05;
}

.kpi-value.small {
  font-size: 1.4rem;
}

.kpi-meta {
  font-size: 13px;
  color: var(--muted);
}

.spark {
  display: block;
  width: 100%;
  height: 36px;
  margin: 6px 0;
  color: var(--green);
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filters .pt-field {
  width: 160px;
}

.cols {
  grid-template-columns: 130px 120px 190px 1fr 90px;
}

.amount {
  font-family: var(--serif);
  font-size: 1.1rem;
}

.muted {
  color: var(--muted);
  font-size: 13.5px;
}

.desc {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--ink-2);
}

@media (max-width: 900px) {
  .kpis {
    grid-template-columns: 1fr;
  }
  .cols {
    grid-template-columns: 1fr 1fr;
  }
  .pt-thead.cols {
    display: none;
  }
}

@media (max-width: 600px) {
  .filters,
  .filters .pt-field {
    width: 100%;
  }
}
</style>